export type ColorKey = 'negro' | 'blanco' | 'azul' | 'verde' | 'rojo';

export type PricingConfig = {
  valorDolar: number;
  preciosPorColor: Record<ColorKey, number>;
  pdfTemplate: {
    companyName: string;
    companySubtitle: string;
    contactTitle: string;
    quoteTitle: string;
    sectionTitle: string;
    legalTitle: string;
    validityLabel: string;
    validityValue: string;
    legalLines: string[];
    colors: {
      headerBg: string;
      accent: string;
      totalBg: string;
      sectionTitle: string;
      textOnDark: string;
      textPrimary: string;
      textMuted: string;
      legalText: string;
      badgeBg: string;
      badgeText: string;
      contactBg: string;
      contactTitle: string;
      contactText: string;
      legalHeaderBg: string;
      legalHeaderText: string;
    };
    fontSizes: {
      companyName: number;
      companySubtitle: number;
      sectionTitle: number;
      body: number;
      total: number;
      legal: number;
      badgeTitle: number;
      badgeSubtitle: number;
      contactTitle: number;
      contactBody: number;
      legalTitle: number;
    };
    layout: {
      headerOffsetY: number;
      infoOffsetY: number;
      contactOffsetY: number;
      tableOffsetY: number;
      conditionsOffsetY: number;
    };
  };
};

const PRECIOS_DEFAULT: Record<ColorKey, number> = {
  negro: 2.5,
  blanco: 2.5,
  azul: 2.6,
  verde: 2.6,
  rojo: 2.7
};

export const PRICING_DEFAULTS: PricingConfig = {
  valorDolar: 1200,
  preciosPorColor: PRECIOS_DEFAULT,
  pdfTemplate: {
    companyName: process.env.REACT_APP_COMPANY_NAME || 'PLASTICOS POLCEN',
    companySubtitle: process.env.REACT_APP_COMPANY_DESCRIPTION || 'Fabricacion y distribucion de bolsas plasticas',
    contactTitle: 'DATOS DE CONTACTO',
    quoteTitle: 'Cotizacion de Precios',
    sectionTitle: 'Detalle del presupuesto',
    legalTitle: 'CONDICIONES DE LA COTIZACION',
    validityLabel: 'Validez',
    validityValue: '30 dias',
    legalLines: [
      'Precios expresados en pesos argentinos.',
      'Sujetos a variacion cambiaria.',
      'Los valores indicados no incluyen IVA.'
    ],
    colors: {
      headerBg: '#171a3b',
      accent: '#24abb0',
      totalBg: '#171a3b',
      sectionTitle: '#171a3b',
      textOnDark: '#ffffff',
      textPrimary: '#1a1a1a',
      textMuted: '#595959',
      legalText: '#404040',
      badgeBg: '#f5f20f',
      badgeText: '#1a1a1a',
      contactBg: '#e8f4f5',
      contactTitle: '#171a3b',
      contactText: '#595959',
      legalHeaderBg: '#171a3b',
      legalHeaderText: '#ffffff',
    },
    fontSizes: {
      companyName: 28,
      companySubtitle: 14,
      sectionTitle: 18,
      body: 14,
      total: 20,
      legal: 12,
      badgeTitle: 12,
      badgeSubtitle: 8,
      contactTitle: 10,
      contactBody: 9,
      legalTitle: 10
    },
    layout: {
      headerOffsetY: 0,
      infoOffsetY: 0,
      contactOffsetY: 0,
      tableOffsetY: 0,
      conditionsOffsetY: 0
    }
  }
};

export const normalizePricingConfig = (parsed: any): PricingConfig => {
  const legacyLegalLines = [
    parsed?.pdfTemplate?.legalLine1,
    parsed?.pdfTemplate?.legalLine2,
    parsed?.pdfTemplate?.legalLine3,
  ].filter(Boolean);

  return {
    valorDolar: Number(parsed?.valorDolar) || PRICING_DEFAULTS.valorDolar,
    preciosPorColor: {
      ...PRICING_DEFAULTS.preciosPorColor,
      ...(parsed?.preciosPorColor || {})
    },
    pdfTemplate: {
      ...PRICING_DEFAULTS.pdfTemplate,
      ...(parsed?.pdfTemplate || {}),
      validityLabel: String(parsed?.pdfTemplate?.validityLabel || PRICING_DEFAULTS.pdfTemplate.validityLabel),
      validityValue: String(parsed?.pdfTemplate?.validityValue || PRICING_DEFAULTS.pdfTemplate.validityValue),
      legalLines:
        Array.isArray(parsed?.pdfTemplate?.legalLines) && parsed.pdfTemplate.legalLines.length > 0
          ? parsed.pdfTemplate.legalLines.map((x: any) => String(x))
          : (legacyLegalLines.length ? legacyLegalLines : PRICING_DEFAULTS.pdfTemplate.legalLines),
      colors: {
        ...PRICING_DEFAULTS.pdfTemplate.colors,
        ...(parsed?.pdfTemplate?.colors || {})
      },
      fontSizes: {
        ...PRICING_DEFAULTS.pdfTemplate.fontSizes,
        ...(parsed?.pdfTemplate?.fontSizes || {})
      },
      layout: {
        ...PRICING_DEFAULTS.pdfTemplate.layout,
        ...(parsed?.pdfTemplate?.layout || {})
      }
    },
  };
};

export const getPricingConfig = (): PricingConfig => {
  try {
    const raw = localStorage.getItem('polcen_pricing_config');
    if (!raw) return PRICING_DEFAULTS;
    return normalizePricingConfig(JSON.parse(raw));
  } catch {
    return PRICING_DEFAULTS;
  }
};

export const savePricingConfig = (config: PricingConfig) => {
  localStorage.setItem('polcen_pricing_config', JSON.stringify(config));
};
